import { callUnaryMethod } from '@connectrpc/connect-query'
import { prepareDownload } from '@jotta/grpc-connect-openapi/contentLocatorQuery'
import { publicTransport } from './transport'
import { getOrRefreshAccessToken } from '@jotta/auth-client/useAuthStatus'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { formDownload } from '@jotta/utils/download'
import type { DownloadLocationRequest } from '@jotta/grpc-connect-openapi/content'
import type { PartialMessage } from '@bufbuild/protobuf'
import type { PathItemObject } from '@jotta/types/Files'
import Debug from 'debug'

const log = Debug('jotta:content')

export async function downloadItems(
  name: string,
  items: { encodedContentRef: string }[] | string[],
  tokenParam?: string,
) {
  log(`downloading ${items.length} items`)

  const elements = items.map(objectOrRef => {
    const encodedContentRef =
      typeof objectOrRef === 'string'
        ? objectOrRef
        : objectOrRef.encodedContentRef
    return {
      ref: {
        case: 'encodedRef',
        value: encodedContentRef,
      },
    } satisfies PartialMessage<DownloadLocationRequest>
  })
  if (elements.length == 0) {
    return
  }

  const token = tokenParam || (await getOrRefreshAccessToken())
  const prepareDownloadResponse = await callUnaryMethod(
    prepareDownload,
    {
      name,
      elements: elements,
    },
    {
      transport: publicTransport,
      callOptions: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    },
  )

  const result = prepareDownloadResponse.result
  switch (result.case) {
    case 'located': {
      return await formDownload(result.value.url, {
        access_token: token,
      })
    }
    case undefined:
    case 'notFound':
    case 'accountLocked':
    case 'accessDenied':
    case 'notSupported':
      throw new Error(result?.case)
    default:
      return exhaustiveGuard(result)
  }
}

export function downloadPathItem(item: PathItemObject) {
  return downloadItems(item.name, [item], item.accessTokenOverride)
}
